import { Button } from "@/components/ui/button";
import { MoveDown, MoveUp, Trash, Upload } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

export type ProductImageType = {
	id?: string;
	product_id?: string;
	image_url: string;
	is_primary?: boolean;
	sequence?: number;
	file?: File; // For new local files
	isNew?: boolean; // To distinguish between existing and new files
};

interface ProductImageManagerProps {
	existingImages?: ProductImageType[];
	selectedFiles: File[];
	setSelectedFiles: (files: File[]) => void;
	isUploading?: boolean;
	uploadProgress?: number;
	onDeleteImage?: (imageId: string) => Promise<void>;
	onReorder?: (images: ProductImageType[]) => Promise<void>;
	onOrderChange?: (images: ProductImageType[]) => void;
}

export function ProductImageManager({
	existingImages = [],
	selectedFiles,
	setSelectedFiles,
	isUploading = false,
	uploadProgress = 0,
	onDeleteImage,
	onReorder,
	onOrderChange,
}: ProductImageManagerProps) {
	const [orderedImages, setOrderedImages] = useState<ProductImageType[]>([]);
	const [manuallyOrdered, setManuallyOrdered] = useState(false);

	// Create object URLs for selected files
	const fileUrls = useMemo(() => {
		return selectedFiles.reduce((acc, file) => {
			acc[file.name] = URL.createObjectURL(file);
			return acc;
		}, {} as Record<string, string>);
	}, [selectedFiles]);

	// Cleanup object URLs on unmount
	useEffect(() => {
		return () => {
			Object.values(fileUrls).forEach(URL.revokeObjectURL);
		};
	}, [fileUrls]);

	// Update orderedImages when props change
	useEffect(() => {
		// Create a map of existing image URLs to avoid duplicates
		const existingUrlMap = new Set(
			existingImages.map((img) => img.image_url)
		);

		// Map new files to image objects
		const newImageObjects = selectedFiles.map((file, index) => ({
			image_url: fileUrls[file.name],
			file,
			isNew: true,
			sequence: existingImages.length + index,
		}));

		// Filter out any duplicate new images
		const uniqueNewImages = newImageObjects.filter(
			(img) => !existingUrlMap.has(img.image_url)
		);

		if (!manuallyOrdered) {
			// If not manually ordered, combine and sort all images
			const allImages = [...existingImages, ...uniqueNewImages].sort(
				(a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)
			);
			setOrderedImages(allImages);
		} else {
			// If manually ordered, only add new images that aren't already in orderedImages
			const orderedUrlMap = new Set(
				orderedImages.map((img) => img.image_url)
			);
			const newImagesToAdd = uniqueNewImages.filter(
				(img) => !orderedUrlMap.has(img.image_url)
			);

			if (newImagesToAdd.length > 0) {
				setOrderedImages((current) => [...current, ...newImagesToAdd]);
			}
		}

		if (onOrderChange) {
			onOrderChange(orderedImages);
		}
	}, [
		existingImages,
		selectedFiles,
		fileUrls,
		manuallyOrdered,
		onOrderChange,
	]);

	const handleFileChange = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const files = e.target.files;
			if (files) {
				const newFiles = Array.from(files);
				setSelectedFiles([...selectedFiles, ...newFiles]);
			}
			e.target.value = "";
		},
		[selectedFiles, setSelectedFiles]
	);

	const moveImage = useCallback(
		async (index: number, direction: "up" | "down") => {
			if (
				(direction === "up" && index === 0) ||
				(direction === "down" && index === orderedImages.length - 1)
			) {
				return;
			}

			setManuallyOrdered(true);
			const swapIndex = direction === "up" ? index - 1 : index + 1;

			const newOrderedImages = [...orderedImages];
			[newOrderedImages[index], newOrderedImages[swapIndex]] = [
				newOrderedImages[swapIndex],
				newOrderedImages[index],
			];

			// Update sequences
			const resequencedImages = newOrderedImages.map((img, idx) => ({
				...img,
				sequence: idx,
				is_primary: idx === 0,
			}));

			setOrderedImages(resequencedImages);

			// Only call onReorder for existing images
			if (onReorder) {
				const existingImagesOnly = resequencedImages.filter(
					(img) => !img.isNew
				);
				await onReorder(existingImagesOnly);
			}

			if (onOrderChange) {
				onOrderChange(resequencedImages);
			}
		},
		[orderedImages, onReorder, onOrderChange]
	);

	const handleDelete = async (index: number) => {
		const imageToDelete = orderedImages[index];

		if (imageToDelete.id && onDeleteImage) {
			await onDeleteImage(imageToDelete.id);
		} else if (imageToDelete.isNew) {
			const newFiles = selectedFiles.filter(
				(file) => fileUrls[file.name] !== imageToDelete.image_url
			);
			setSelectedFiles(newFiles);
		}

		// Update local state
		const newOrderedImages = orderedImages
			.filter((_, idx) => idx !== index)
			.map((img, idx) => ({
				...img,
				sequence: idx,
				is_primary: idx === 0,
			}));
		setOrderedImages(newOrderedImages);

		if (onOrderChange) {
			onOrderChange(newOrderedImages);
		}
	};

	return (
		<div className="space-y-4">
			<div>
				<h3 className="text-lg font-medium">Product Images</h3>
				<p className="text-sm text-gray-500">
					Upload high-quality images of your product. The first image
					will be used as the primary image.
				</p>
			</div>

			{/* Image Grid */}
			<div className="grid grid-cols-2 md:grid-cols-3 gap-4">
				{orderedImages.map((image, index) => (
					<div
						key={image.id || `new-${index}`}
						className="relative group border rounded-md overflow-hidden">
						<img
							src={image.image_url}
							alt={`Product ${index + 1}`}
							className="w-full aspect-square object-cover"
						/>

						{index === 0 && (
							<span className="absolute top-2 left-2 bg-primary/10 text-primary px-2 py-1 rounded text-xs">
								Primary
							</span>
						)}

						<div className="absolute bottom-2 right-2 flex space-x-1">
							{index > 0 && (
								<Button
									type="button"
									size="icon"
									variant="secondary"
									className="h-8 w-8 opacity-90"
									onClick={() => moveImage(index, "up")}>
									<MoveUp className="h-4 w-4" />
								</Button>
							)}

							{index < orderedImages.length - 1 && (
								<Button
									type="button"
									size="icon"
									variant="secondary"
									className="h-8 w-8 opacity-90"
									onClick={() => moveImage(index, "down")}>
									<MoveDown className="h-4 w-4" />
								</Button>
							)}

							<Button
								type="button"
								size="icon"
								variant="destructive"
								className="h-8 w-8 opacity-90"
								onClick={() => handleDelete(index)}>
								<Trash className="h-4 w-4" />
							</Button>
						</div>
					</div>
				))}
			</div>

			{/* File Upload Button */}
			<div className="border-2 border-dashed border-gray-300 rounded-md p-4 text-center">
				<label className="cursor-pointer">
					<div className="flex flex-col items-center gap-2">
						<Upload className="h-8 w-8 text-gray-400" />
						<span className="font-medium">Upload Images</span>
						<span className="text-xs text-gray-500">
							PNG, JPG, WEBP up to 5MB
						</span>
					</div>
					<input
						type="file"
						accept="image/*"
						multiple
						onChange={handleFileChange}
						className="hidden"
						disabled={isUploading}
					/>
				</label>
			</div>

			{/* Upload Progress */}
			{isUploading && (
				<div className="w-full bg-gray-200 rounded-full h-2.5">
					<div
						className="bg-primary h-2.5 rounded-full"
						style={{ width: `${uploadProgress}%` }}
					/>
				</div>
			)}
		</div>
	);
}
